import { Link } from 'react-router-dom';
import PencilIcon from './PencilIcon';

/**
 * Home's one message block.
 *
 * Which message shows is decided by `useLadderState`, **once per page load** -
 * see that file for the states and why it latches. This component only renders
 * the copy for a given state.
 *
 * It is a block, but deliberately not the *same* block as everything else on
 * Home: no `SectionBlock`, no notched label, no hairline white border. A
 * gradient edge and a faint colour wash instead, so it reads as the page
 * talking to you rather than as one more feature to deal with.
 *
 * (It was briefly unpanelled entirely, which went too far the other way and
 * left the guidance looking like stray text.)
 */


const Line = ({ children }) => (
  <p className="text-sm text-nlg-text leading-relaxed">{children}</p>
);

const Heading = ({ children }) => (
  <p className="font-display font-black text-lg leading-tight text-nlg-cream">{children}</p>
);

// The one word in a line worth catching the eye. `text-nlg-gold` rather than
// `text-nlg-yellow` - the two tokens hold the same value (#f5d76e), and gold
// is what every other piece of emphasis on the site uses.
const Highlight = ({ children }) => (
  <span className="font-bold text-nlg-gold">{children}</span>
);

// A line with art beside it. `flip` puts the image on the right and the plate
// to its left, so a stack of these zigzags rather than sitting in one column.
//
// The plate is the <p>'s own background rather than a separate absolute layer,
// which is what makes it only as wide as its text: a flex item sizes to its
// content unless told otherwise, so there's no `flex-1` here. It slides under
// the image with a negative margin on the facing side, and the padding on that
// same side puts the text back clear of the circle. Short enough (one line of
// text plus `py-1`) that the 60px image breaks out of it top and bottom.
//
// The negative margin is measured from the image's trailing edge, so the 8px
// of clearance between circle and text survives a change of image size on its
// own - only the two numbers below would need touching if that gap moved.
//
// The image carries `z-10` so it stays on top: both are positioned, and
// without it the <p> would paint over the image simply by coming later.
//
// `text-left` is explicit, not redundant: `MainLayout` puts `text-center` on
// the whole app wrapper, so anything that doesn't say otherwise inherits it.
// That's why the flipped row looked right from the start and these two didn't.
const IllustratedLine = ({ image, flip = false, children }) => (
  <div className={`flex items-center ${flip ? 'flex-row-reverse' : ''}`}>
    <img
      src={image}
      alt=""
      className="relative z-10 w-[60px] h-[60px] rounded-full border-2 border-black flex-shrink-0"
    />
    <p
      className={`relative rounded-xl bg-black/30 py-1 text-sm text-nlg-text leading-relaxed ${
        flip ? 'text-right -mr-5 pr-7 pl-3' : 'text-left -ml-5 pl-7 pr-3'
      }`}
    >
      {children}
    </p>
  </div>
);

/**
 * One entry per ladder state. Indexed by the number, so state 5 rendering
 * nothing is simply a null.
 */
export const MESSAGES = [
  // 0 - no record at all. Alternating art, so the intro reads as three beats
  // rather than a list. love.gif throughout is a placeholder - each line wants
  // its own picture eventually.
  () => (
    // Its own spacing, wider than IllustratedLine's internal gap - that
    // contrast is what makes each picture read as belonging to the line beside
    // it. As the single child here, this overrides the block's `space-y-1`.
    <div className="-space-y-5">
      <IllustratedLine image="/img/treasure.gif">
        <Highlight>Discover</Highlight> new daily puzzles
      </IllustratedLine>
      <IllustratedLine image="/img/hearts.gif" flip>
        Build your own <Highlight>playlist</Highlight>
      </IllustratedLine>
      <IllustratedLine image="/img/trophy.gif">
        Share and play with <Highlight>friends</Highlight>
      </IllustratedLine>
    </div>
  ),

  // 1 - has a record, nothing favorited yet. **Deliberately silent.** The
  // heart/✕ instruction that used to live here is in the Editor's Picks block,
  // sitting next to the actual buttons - two copies of it on one page was
  // worse than one copy in the right place.
  null,

  // 2 - has a playlist, has never logged a score.
  () => (
    <>
      <img
        src="/img/trophy.gif"
        alt=""
        className="w-[60px] h-[60px] rounded-lg border-2 border-black float-right ml-3"
      />
      <Line>
        Click

        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full align-middle mx-1 
             bg-nlg-gold text-nlg-bg mx-2`}>
          <PencilIcon className="w-3.5 h-3.5"/>
        </span>
        to track your scores
      </Line>
      <Line>to share them and play with friends</Line>
    </>
  ),

  // 3 - has scored, but has no credentials. Points at SIGNUP, not login:
  // signing up attaches credentials to the record they already hold, while
  // signing into a different account abandons it (D42).
  // The whole message is the link, not just the words "Sign in" - the same
  // treatment the old "Make yourself at home" panel used. "Sign in" stays gold
  // and bold so the affordance is still obvious, but it's a <span> now: an <a>
  // inside an <a> is invalid, and it would have left most of the message
  // looking inert next to one live word.
  () => (
    <Link
      to="/signup"
      className="block space-y-1 overflow-hidden transition-opacity duration-150 hover:opacity-80"
    >
      <img
        src="/img/login.gif"
        alt=""
        className="w-[60px] h-[60px] rounded-lg border-2 border-black float-right ml-3"
      />
      <Line>
        <Highlight>Sign in</Highlight> to keep your playlist and scores safe and synced up
      </Line>
    </Link>
  ),

  // 4 - signed in, has never shared. The link opens the share panel inside the
  // Scores block further down this page rather than navigating.
  () => (
    <>
      <img
        src="/img/trophy.gif"
        alt=""
        className="w-[60px] h-[60px] rounded-lg border-2 border-black float-right ml-3"
      />
      <Heading>Play with friends</Heading>
      <Line>Share all today's scores in one post</Line>
      <Line>
        <Link to="/#share" className="font-bold text-nlg-gold">Invite friends</Link> to follow each others' scores
      </Line>
    </>
  ),

  // 5 - nothing left to say.
  null,
];

function HomeMessage({ state }) {
  const render = state === null || state === undefined ? null : MESSAGES[state];
  if (!render) return null;

  return (
    // Deliberately *not* a SectionBlock. Every other block on Home is a flat
    // panel with a hairline white border and a label notched into it; this one
    // is a gradient-edged card with a colour wash and no label, so it reads as
    // the page talking to you rather than as one more feature to deal with.
    //
    // The gradient edge is a 1.5px padded wrapper with the real surface inside
    // it - CSS can't gradient a border directly. The inner radius is a touch
    // smaller than the outer so the curves stay concentric.
    // Both gradients are sized at 400% and orbited (see `nlg-aurora` and
    // `gradient-drift` in tailwind.config.js), so the colour moves while the
    // element stays put. `motion-reduce:animate-none` leaves a perfectly good
    // static gradient for anyone who has asked the OS for less movement -
    // this is ambience, and nothing depends on it.
    <div className="mx-1 mb-4 rounded-2xl p-[1.5px] bg-nlg-aurora bg-[length:400%_400%] animate-gradient-drift motion-reduce:animate-none">
      <div className="relative rounded-[15px] bg-nlg-block px-2 py-2 overflow-hidden">
        {/* The wash. Drifts on a longer cycle than the edge, and in the
            opposite phase for part of it, so the two never move together. */}
        <div
          className="pointer-events-none absolute inset-0 bg-nlg-aurora-wash bg-[length:400%_400%] animate-gradient-drift-slow motion-reduce:animate-none"
          aria-hidden="true"
        />
        {/* The card stays full width - it's the *content* that's capped at
            460px and centred in whatever is left over, so the copy doesn't
            stretch into a single long line on a wide viewport.
            overflow-hidden here, not just on the parent: it contains the
            floated art some states carry, so a 50px image in a two-line block
            can't hang past the bottom. Clipping it would only hide the
            symptom. */}
        <div className="relative mx-auto max-w-[460px] space-y-1 overflow-hidden">
          {render()}
        </div>
      </div>
    </div>
  );
}

export default HomeMessage;
